(() => {
  if (window.__stoneHausDeliveryEstimatorBooted) {
    if (window.StoneHausDeliveryEstimatorScan) window.StoneHausDeliveryEstimatorScan(document);
    return;
  }
  window.__stoneHausDeliveryEstimatorBooted = true;

  const SELECTOR = '[data-sh-delivery-estimator]';
  const STORAGE_KEY = 'stonehaus_delivery_postcode';
  const PHONE_LABEL = '0492 93 6000';
  const PHONE_HREF = 'tel:+61492936000';
  const MOBILE_QUERY = '(max-width: 749px)';

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function installUiFixes() {
    if (document.getElementById('sh-delivery-estimator-hotfixes')) return;
    const style = document.createElement('style');
    style.id = 'sh-delivery-estimator-hotfixes';
    style.textContent = `
      /* Stone Haus: permanently disable the theme sticky product CTA. */
      .product-section .wt-product__sticky-buy,
      .product-section sticky-buy-button.wt-product__sticky-buy {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }

      /* Freight quote modal: always use the real viewport, not a product/card containing block. */
      .sh-freight-modal {
        position: fixed !important;
        z-index: 10050 !important;
        inset: 0 !important;
        width: 100vw !important;
        min-height: 100vh !important;
        box-sizing: border-box !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        padding: 20px !important;
        background: rgba(18, 19, 18, .52) !important;
      }
      @supports (height: 100dvh) {
        .sh-freight-modal { min-height: 100dvh !important; height: 100dvh !important; }
      }
      .sh-freight-modal[hidden] { display: none !important; }
      .sh-freight-modal__panel {
        width: min(560px, 100%) !important;
        max-height: calc(100vh - 40px) !important;
        overflow-y: auto !important;
        overscroll-behavior: contain !important;
        -webkit-overflow-scrolling: touch;
      }
      @supports (height: 100dvh) {
        .sh-freight-modal__panel { max-height: calc(100dvh - 40px) !important; }
      }
      html.sh-freight-modal-open,
      body.sh-freight-modal-open {
        overflow: hidden !important;
        overscroll-behavior: none !important;
      }

      @media (max-width: 749px) {
        .sh-freight-modal {
          align-items: flex-end !important;
          justify-content: center !important;
          padding: max(8px, env(safe-area-inset-top)) 0 0 !important;
        }
        .sh-freight-modal__panel {
          width: 100% !important;
          max-width: none !important;
          max-height: calc(100vh - max(8px, env(safe-area-inset-top))) !important;
          margin: 0 !important;
          padding: 20px 16px calc(18px + env(safe-area-inset-bottom)) !important;
          border-radius: 18px 18px 0 0 !important;
          transform: translateY(28px) !important;
          box-shadow: 0 -10px 40px rgba(0, 0, 0, .24) !important;
        }
        @supports (height: 100dvh) {
          .sh-freight-modal__panel {
            max-height: calc(100dvh - max(8px, env(safe-area-inset-top))) !important;
          }
        }
        .sh-freight-modal.is-open .sh-freight-modal__panel {
          transform: translateY(0) !important;
        }
        .sh-freight-modal__close {
          top: 10px !important;
          right: 12px !important;
        }
        .sh-freight-modal__header {
          padding-right: 38px !important;
          margin-bottom: 14px !important;
        }
        .sh-freight-modal__fields {
          grid-template-columns: 1fr !important;
          gap: 10px !important;
        }
        .sh-freight-modal__field--full { grid-column: auto !important; }
      }
    `;
    document.head.appendChild(style);
  }

  function cleanPostcode(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 4);
  }

  function stateForPostcode(postcode) {
    const n = Number(postcode);
    if (!/^\d{4}$/.test(postcode)) return null;
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) {
      return { code: 'ACT', name: 'Australian Capital Territory' };
    }
    if (n >= 800 && n <= 999) return { code: 'NT', name: 'Northern Territory' };
    if ((n >= 2000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) {
      return { code: 'NSW', name: 'New South Wales' };
    }
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return { code: 'VIC', name: 'Victoria' };
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return { code: 'QLD', name: 'Queensland' };
    if (n >= 5000 && n <= 5999) return { code: 'SA', name: 'South Australia' };
    if (n >= 6000 && n <= 6999) return { code: 'WA', name: 'Western Australia' };
    if (n >= 7000 && n <= 7999) return { code: 'TAS', name: 'Tasmania' };
    return null;
  }

  function isSydneyMetro(postcode) {
    const n = Number(postcode);
    return (n >= 2000 && n <= 2234) || (n >= 2555 && n <= 2574) || (n >= 2740 && n <= 2770);
  }

  function regionForPostcode(postcode) {
    const state = stateForPostcode(postcode);
    if (!state) return { key: 'unknown', label: 'Delivery area', state: null };
    if (state.code === 'NSW' && isSydneyMetro(postcode)) return { key: 'sydney', label: 'Sydney Metro', state };
    if (state.code === 'NSW') return { key: 'regional-nsw', label: 'Regional NSW', state };
    return { key: 'interstate', label: 'Interstate', state };
  }

  function formatMoney(value, currency) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    try {
      return new Intl.NumberFormat('en-AU', {
        style: 'currency',
        currency: currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'AUD',
        minimumFractionDigits: number % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2
      }).format(number);
    } catch (error) {
      return '$' + number.toFixed(number % 1 === 0 ? 0 : 2);
    }
  }

  function resultNodes(root) {
    return {
      result: root.querySelector('[data-sh-delivery-result]'),
      button: root.querySelector('[data-sh-delivery-submit]'),
      input: root.querySelector('[data-sh-delivery-postcode]')
    };
  }

  function appendQuoteActions(result) {
    const actions = document.createElement('div');
    actions.className = 'sh-delivery-estimator__quote-actions';

    const call = document.createElement('a');
    call.className = 'sh-delivery-estimator__quote-action sh-delivery-estimator__quote-action--call';
    call.href = PHONE_HREF;
    call.textContent = 'Call ' + PHONE_LABEL;

    const quote = document.createElement('button');
    quote.type = 'button';
    quote.className = 'sh-delivery-estimator__quote-action sh-delivery-estimator__quote-action--form';
    quote.setAttribute('data-sh-freight-open', '');
    quote.textContent = 'Request freight quote';

    actions.append(call, quote);
    result.appendChild(actions);
  }

  function setResult(root, options) {
    const { result } = resultNodes(root);
    if (!result) return;
    result.replaceChildren();
    result.hidden = false;
    result.dataset.tone = options.tone || 'neutral';

    const top = document.createElement('div');
    top.className = 'sh-delivery-estimator__result-top';
    const label = document.createElement('strong');
    label.className = 'sh-delivery-estimator__result-label';
    label.textContent = options.label || 'Delivery';
    const primary = document.createElement('span');
    primary.className = 'sh-delivery-estimator__result-primary';
    primary.textContent = options.primary || '';
    top.append(label, primary);
    result.appendChild(top);

    if (options.detail) {
      const detail = document.createElement('p');
      detail.className = 'sh-delivery-estimator__result-detail';
      detail.textContent = options.detail;
      result.appendChild(detail);
    }

    if (Array.isArray(options.rates) && options.rates.length) {
      const list = document.createElement('div');
      list.className = 'sh-delivery-estimator__rates';
      options.rates.slice(0, 3).forEach((rate) => {
        const row = document.createElement('div');
        row.className = 'sh-delivery-estimator__rate';
        const name = document.createElement('span');
        name.textContent = rate.name || 'Freight';
        const price = document.createElement('strong');
        price.textContent = rate.formattedPrice || '';
        row.append(name, price);
        list.appendChild(row);
      });
      result.appendChild(list);
    }

    if (options.call) appendQuoteActions(result);
  }

  function fallbackForRegion(root, region, prefix) {
    const lead = prefix ? prefix + ' · ' : '';
    if (region.key === 'sydney') {
      setResult(root, {
        tone: 'quote',
        label: region.label,
        primary: lead + 'freight quote required',
        detail: 'Sydney delivery is typically 2–3 working days. Cost varies with order weight, quantity and unloading requirements.',
        call: true
      });
      return;
    }
    if (region.key === 'regional-nsw') {
      setResult(root, {
        tone: 'quote',
        label: region.label,
        primary: lead + 'freight quote required',
        detail: 'Regional delivery is typically 4–8 working days. We confirm freight based on the order and delivery site.',
        call: true
      });
      return;
    }
    if (region.key === 'interstate') {
      setResult(root, {
        tone: 'quote',
        label: region.label,
        primary: lead + 'freight quote required',
        detail: 'We deliver across QLD, VIC and most Australian regions using third-party freight partners.',
        call: true
      });
      return;
    }
    setResult(root, {
      tone: 'quote',
      label: 'Delivery area',
      primary: lead + 'freight quote required',
      detail: 'We could not match that postcode to an Australian delivery zone. Please contact us and we will confirm freight.',
      call: true
    });
  }

  function productGuidance(root, region) {
    if (region.key === 'sydney') {
      setResult(root, {
        tone: 'good',
        label: region.label,
        primary: 'Delivery available',
        detail: 'Typically 2–3 working days. Add your required quantity to cart to check a live freight price; final cost depends on order weight and unloading requirements.'
      });
      return;
    }
    if (region.key === 'regional-nsw') {
      setResult(root, {
        tone: 'quote',
        label: region.label,
        primary: 'Freight quote required',
        detail: 'Typically 4–8 working days. We confirm the freight cost from the quantity, pallet weight and delivery site.',
        call: true
      });
      return;
    }
    if (region.key === 'interstate') {
      setResult(root, {
        tone: 'quote',
        label: region.label,
        primary: 'Freight quote required',
        detail: 'We deliver across QLD, VIC and most Australian regions. Freight is confirmed for the order before dispatch.',
        call: true
      });
      return;
    }
    fallbackForRegion(root, region, 'Postcode not recognised');
  }

  async function cartRates(postcode, state) {
    const rootPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const base = rootPath.endsWith('/') ? rootPath : rootPath + '/';
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', postcode);
    params.set('shipping_address[country]', 'Australia');
    if (state && state.name) params.set('shipping_address[province]', state.name);
    const query = params.toString();

    const prepared = await fetch(base + 'cart/prepare_shipping_rates.json?' + query, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!prepared.ok) throw new Error('rate-unavailable');

    for (let attempt = 0; attempt < 7; attempt += 1) {
      if (attempt) await sleep(attempt < 3 ? 450 : 750);
      const response = await fetch(base + 'cart/async_shipping_rates.json?' + query, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('rate-unavailable');
      const data = await response.json();
      if (data && Array.isArray(data.shipping_rates)) return data.shipping_rates;
    }
    throw new Error('rate-timeout');
  }

  async function handleSubmit(root, event) {
    event.preventDefault();
    const { input, button } = resultNodes(root);
    if (!input || !button) return;
    const postcode = cleanPostcode(input.value);
    input.value = postcode;
    if (!/^\d{4}$/.test(postcode)) {
      setResult(root, {
        tone: 'error',
        label: 'Postcode required',
        primary: 'Enter a 4-digit Australian postcode',
        detail: 'For example: 2049.'
      });
      input.focus();
      return;
    }

    try { localStorage.setItem(STORAGE_KEY, postcode); } catch (error) {}
    const region = regionForPostcode(postcode);
    const context = root.dataset.context || 'product';

    if (context === 'product') {
      productGuidance(root, region);
      return;
    }

    button.disabled = true;
    const original = button.textContent;
    button.textContent = 'Checking…';
    setResult(root, {
      tone: 'neutral',
      label: region.label,
      primary: 'Checking current freight rates',
      detail: 'Using the items and quantities currently in your cart.'
    });

    try {
      const rates = await cartRates(postcode, region.state);
      const usable = rates
        .map((rate) => {
          const price = Number(rate.price);
          return Number.isFinite(price) ? {
            name: rate.name || rate.presentment_name || 'Freight',
            price,
            currency: rate.currency || null,
            formattedPrice: formatMoney(price, rate.currency || null)
          } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.price - b.price);

      if (!usable.length) {
        fallbackForRegion(root, region, 'No instant rate available');
        return;
      }

      const min = usable[0];
      const max = usable[usable.length - 1];
      const same = Math.abs(min.price - max.price) < 0.005;
      const priceText = same ? min.formattedPrice : min.formattedPrice + '–' + max.formattedPrice;

      setResult(root, {
        tone: 'good',
        label: region.label,
        primary: 'Estimated delivery ' + priceText,
        detail: 'Based on the products and quantities currently in your cart. Final freight is confirmed at checkout.',
        rates: usable
      });
    } catch (error) {
      fallbackForRegion(root, region, 'Live rate unavailable');
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  }

  function initFreightModal(root, postcodeInput) {
    const modal = root.querySelector('[data-sh-freight-modal]');
    if (!modal || modal.dataset.shFreightReady === 'true') return;
    modal.dataset.shFreightReady = 'true';

    const panel = modal.querySelector('[data-sh-freight-panel]');
    const postcodeField = modal.querySelector('[data-sh-freight-postcode]');
    const nameField = modal.querySelector('[data-sh-freight-name]');
    const closeButton = modal.querySelector('[data-sh-freight-close]');
    let returnFocus = null;
    let hideTimer = null;

    if (modal.parentElement !== document.body) document.body.appendChild(modal);

    const setPageLock = (locked) => {
      document.body.classList.toggle('sh-freight-modal-open', locked);
      document.documentElement.classList.toggle('sh-freight-modal-open', locked);
    };

    const close = () => {
      if (modal.hidden) return;
      window.clearTimeout(hideTimer);
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      setPageLock(false);
      hideTimer = window.setTimeout(() => {
        modal.hidden = true;
        if (returnFocus && typeof returnFocus.focus === 'function') {
          try { returnFocus.focus({ preventScroll: true }); } catch (error) { returnFocus.focus(); }
        }
      }, 170);
    };

    const focusOnOpen = () => {
      const mobile = window.matchMedia(MOBILE_QUERY).matches;
      const target = mobile
        ? (closeButton || panel)
        : (nameField || modal.querySelector('input:not([type="hidden"]), textarea, button'));
      if (!target || typeof target.focus !== 'function') return;
      if (target === panel && !panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');
      try { target.focus({ preventScroll: true }); } catch (error) { target.focus(); }
    };

    const open = (trigger) => {
      returnFocus = trigger || document.activeElement;
      window.clearTimeout(hideTimer);

      const postcode = cleanPostcode(postcodeInput && postcodeInput.value);
      if (postcodeField && /^\d{4}$/.test(postcode)) {
        const current = cleanPostcode(postcodeField.value);
        if (!current || current.length < 4) postcodeField.value = postcode;
      }

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      setPageLock(true);
      requestAnimationFrame(() => {
        modal.classList.add('is-open');
        requestAnimationFrame(focusOnOpen);
      });
    };

    root.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-sh-freight-open]');
      if (!trigger) return;
      event.preventDefault();
      open(trigger);
    });

    modal.addEventListener('click', (event) => {
      const clickedClose = event.target.closest('[data-sh-freight-close]');
      if (clickedClose) {
        event.preventDefault();
        close();
        return;
      }
      if (event.target === modal) close();
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;

      const focusables = [...panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter((element) => element.offsetParent !== null);

      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const formState = modal.querySelector('[data-sh-freight-form-state]');
    if (formState && (formState.dataset.success === 'true' || formState.dataset.errors === 'true')) {
      window.setTimeout(() => open(), 40);
    }
  }

  function init(root) {
    if (!root || root.dataset.shDeliveryReady === 'true') return;
    root.dataset.shDeliveryReady = 'true';
    const form = root.querySelector('[data-sh-delivery-form]');
    const input = root.querySelector('[data-sh-delivery-postcode]');
    if (!form || !input) return;
    try {
      const saved = cleanPostcode(localStorage.getItem(STORAGE_KEY));
      if (saved && !input.value) input.value = saved;
    } catch (error) {}
    input.addEventListener('input', () => { input.value = cleanPostcode(input.value); });
    form.addEventListener('submit', (event) => handleSubmit(root, event));
    initFreightModal(root, input);
  }

  function scan(scope) {
    if (!scope) return;
    if (scope.matches && scope.matches(SELECTOR)) init(scope);
    if (scope.querySelectorAll) scope.querySelectorAll(SELECTOR).forEach(init);
  }
  window.StoneHausDeliveryEstimatorScan = scan;

  const boot = () => {
    installUiFixes();
    scan(document);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) scan(node);
      }));
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
